import {
    LOGIN_FAILURE,
    LOGIN_LOAD,
    LOGIN_SUCCESS,
    VERIFICATION_LOAD,
    VERIFICATION_SEND_FAILURE,
    VERIFICATION_SEND_SUCCESS
} from '../Login/loginActions';
import { USER_ATTRIBUTES_SUCCESS } from './userDataActions';
import {
    get,
    isEqual,
    includes,
    trim
} from 'lodash';
import { createSelector } from 'reselect';
import { getEntityByName } from '../appEntities';

export default function reducer(state = {}, action = {}) {
    let newState;
    switch (action.type) {
        case LOGIN_LOAD:
            newState = {
                ...state,
                error: null,
                loginLoading: true
            };

            return isEqual(state, newState) ? state : newState;

        case LOGIN_SUCCESS:
            newState = {
                ...state,
                isLoggedIn: true,
                loginLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case LOGIN_FAILURE:
            newState = {
                ...state,
                error: action.error,
                loginLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case VERIFICATION_LOAD:
            newState = {
                ...state,
                sendVerificationCodeError: null,
                verificationLoading: true
            };

            return isEqual(state, newState) ? state : newState;

        case VERIFICATION_SEND_FAILURE:
            newState = {
                ...state,
                sendVerificationCodeError: action.error,
                verificationLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case VERIFICATION_SEND_SUCCESS:
            newState = {
                ...state,
                sendVerificationCodeError: null,
                verificationLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case USER_ATTRIBUTES_SUCCESS:
            newState = {
                ...state,
                userAttributes: action.userAttributes
            };

            return isEqual(state, newState) ? state : newState;

        default:
            return state;
    }
}

export const getUserDataEntity = createSelector(
    (state) => state,
    (state) => getEntityByName(state, 'userData')
);

export const getIsLoggedIn = createSelector(
    getUserDataEntity,
    (entity) => get(entity, 'isLoggedIn')
);

export const getLoginError = createSelector(
    getUserDataEntity,
    (entity) => get(entity, 'error')
);

export const getLoginErrorMessage = createSelector(
    getLoginError,
    (error) => get(error, 'message')
);

export const getSendVerificationCodeError = createSelector(
    getUserDataEntity,
    (entity) => get(entity, 'sendVerificationCodeError')
);

export const getSendVerificationCodeErrorMessage = createSelector(
    getSendVerificationCodeError,
    (error) => {
        if (get(error, 'code') === 'NotAuthorizedException') {
            return 'It looks like you\'ve never logged in before. Check your email for an invitation with your temporary password';
        }

        return get(error, 'message');
    }
);

export const getUserAttributes = createSelector(
    getUserDataEntity,
    (entity) => get(entity, 'userAttributes')
);

export const getUserAttributeByName = createSelector(
    getUserAttributes,
    (state, name) => name,
    (userAttributes = [], name) => {
        const userAttribute = userAttributes.find((attribute = {}) => (
            attribute.Name === name
        ));

        return get(userAttribute, 'Value');
    }
);

export const getEmail = createSelector(
    (state) => getUserAttributeByName(state, 'email'),
    (email) => email
);

export const getAccountName = createSelector(
    (state) => getUserAttributeByName(state, 'custom:accountName'),
    (accountName) => accountName
);

export const getAccessRights = createSelector(
    (state) => getUserAttributeByName(state, 'custom:accessRights'),
    (accessRights) => {
        try {
            return JSON.parse(trim(accessRights, '"'));
        } catch (error) {
            // Swallow errors
        }
    }
);

export const getMerchantsAccessRights = createSelector(
    getAccessRights,
    (accessRights) => get(accessRights, 'merchantIds')
);

export const getAccessRightsForMerchant = createSelector(
    getMerchantsAccessRights,
    (state, merchantId) => merchantId,
    (merchantAccessRights, merchantId) => get(merchantAccessRights, merchantId)
);

export const getDoesUserHaveWriteAccessForMerchant = createSelector(
    getAccessRightsForMerchant,
    (state, merchantId) => merchantId,
    (userMerchantAccessRights) => {
        const rolesWithWriteAccess = ['A', 'W'];

        return includes(rolesWithWriteAccess, userMerchantAccessRights);
    }
);

export const getUserRole = createSelector(
    getAccessRights,
    (accessRights) => get(accessRights, 'role')
);

export const getIsUserAnAdmin = createSelector(
    getUserRole,
    (role) => (role === 'A')
);

export const getIsLoginLoading = createSelector(
    getUserDataEntity,
    (entity) => get(entity, 'loginLoading')
);

export const getIsVerificationLoading = createSelector(
    getUserDataEntity,
    (entity) => get(entity, 'verificationLoading')
);
